import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import type { Bot } from '../types/api.types';
import { useChatStore } from '../../features/chat/store/chatStore';

/**
 * Bot Store
 *
 * Manages bot selection and available bots for the user.
 * Handles bot switching and ensures chat state is properly cleared when changing bots.
 *
 * Dependencies:
 * - chatStore: Clears messages and updates currentBot when switching bots
 *
 * Cleared by:
 * - useAuth hook during logout (via clearBotSelection)
 *
 * Important: Both setSelectedBot and switchBot methods handle chat clearing
 * when switching to a different bot. This ensures chat history doesn't leak
 * between different bot conversations.
 */

interface BotState {
  selectedBot: Bot | null;
  selectedWorkspace: string | null;
  availableBots: Bot[];
  defaultBot: Bot | null;
}

interface BotActions {
  setSelectedBot: (
    bot: Bot | null,
    options?: { forceNewChat?: boolean; skipClear?: boolean }
  ) => void;
  setSelectedWorkspace: (workspace: string | null) => void;
  setAvailableBots: (bots: Bot[]) => void;
  switchBot: (botId: string) => void;
  clearBotSelection: () => void;
  setDefaultBot: (bot: Bot | null) => void;
  applyDefaultBot: () => void;
}

type BotStore = BotState & BotActions;

const initialState: BotState = {
  selectedBot: null,
  selectedWorkspace: null,
  availableBots: [],
  defaultBot: null,
};

export const useBotStore = create<BotStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        setSelectedBot: (bot, options) => {
          const { selectedBot } = get();

          if (options?.forceNewChat && bot) {
            // Force new chat with this bot - use the new method
            useChatStore.getState().newChatWithBot(bot);
          } else if (
            bot &&
            selectedBot &&
            bot._id !== selectedBot._id &&
            !options?.skipClear
          ) {
            // Only clear messages when actively switching bots (not during conversation loading)
            useChatStore.getState().clearMessages();
          }

          set({ selectedBot: bot }, false, 'setSelectedBot');

          // Update the current bot in chat store (only if not forcing new chat, since newChatWithBot already sets it)
          if (bot && !options?.forceNewChat) {
            useChatStore.getState().setCurrentBot(bot);
          }
        },

        setSelectedWorkspace: workspace =>
          set({ selectedWorkspace: workspace }, false, 'setSelectedWorkspace'),

        setAvailableBots: bots =>
          set({ availableBots: bots }, false, 'setAvailableBots'),

        switchBot: botId => {
          const { availableBots, selectedBot } = get();
          const bot = availableBots.find(b => b._id === botId);

          if (bot && bot._id !== selectedBot?._id) {
            // Clear chat messages when switching to a different bot
            useChatStore.getState().clearMessages();

            // Update selected bot
            set({ selectedBot: bot }, false, 'switchBot');

            // Update the current bot in chat store
            useChatStore.getState().setCurrentBot(bot);
          }
        },

        clearBotSelection: () => {
          set({ selectedBot: null }, false, 'clearBotSelection');
          useChatStore.getState().setCurrentBot(null);
        },

        setDefaultBot: bot => {
          if (!bot) {
            set({ defaultBot: null }, false, 'setDefaultBot:clear');
            return;
          }

          const templateBot: Bot = {
            ...bot,
            // sessionId: undefined,
          };

          set({ defaultBot: templateBot }, false, 'setDefaultBot');
        },

        applyDefaultBot: () => {
          const { defaultBot } = get();

          if (!defaultBot) {
            set({ selectedBot: null }, false, 'applyDefaultBot:clear');
            useChatStore.getState().setCurrentBot(null);
            return;
          }

          const botInstance: Bot = {
            ...defaultBot,
            // sessionId: generateSessionId(),
          };
          set({ selectedBot: botInstance }, false, 'applyDefaultBot:set');
          useChatStore.getState().setCurrentBot(botInstance);
        },
      }),
      {
        name: 'bot-unify-bots',
        version: 2,
        partialize: (state): Partial<BotState> => ({
          // Only persist selectedWorkspace - selectedBot should be session-only
          selectedWorkspace: state.selectedWorkspace,
          // Explicitly exclude selectedBot and availableBots from persistence
        }),
        migrate: (persistedState: unknown, version: number) => {
          if (version === 0) {
            // Migration from version 0 to 1
            const state = persistedState as Partial<BotState>;
            return {
              ...state,
              // Add any new fields with defaults
              availableBots: state.availableBots || [],
            };
          }
          if (version === 1) {
            // Migration from version 1 to 2
            // Remove persisted selectedBot to fix preview bot persistence issue
            const state = persistedState as Partial<BotState>;
            return {
              selectedWorkspace: state.selectedWorkspace,
              selectedBot: null, // Clear any persisted selectedBot
              availableBots: [],
            };
          }
          return persistedState as BotState;
        },
      }
    ),
    {
      name: 'BotStore',
    }
  )
);

// Optimized selectors with shallow comparison
export const useSelectedBot = () =>
  useBotStore(state => state.selectedBot, shallow);
export const useSelectedWorkspace = () =>
  useBotStore(state => state.selectedWorkspace);
export const useAvailableBots = () =>
  useBotStore(state => state.availableBots, shallow);

// Composite selector for bot selection state
export const useBotSelectionState = () =>
  useBotStore(
    state => ({
      selectedBot: state.selectedBot,
      selectedWorkspace: state.selectedWorkspace,
    }),
    shallow
  );

// Action selectors for performance
export const useBotActions = () =>
  useBotStore(
    state => ({
      setSelectedBot: state.setSelectedBot,
      switchBot: state.switchBot,
      clearBotSelection: state.clearBotSelection,
    }),
    shallow
  );
